import { useCallback, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

/**
 * Raw SDK message from the NDJSON stream. These arrive as-is from the Claude
 * Agent SDK — the API forwards them with zero translation.
 *
 * Key types: system, stream_event, assistant, user, result, tool_progress
 */
type SdkMessage = Record<string, unknown>;

/**
 * Map a raw SDK message into the internal event shape consumed by UI components.
 * SDK messages arrive with a `type` field (system, stream_event, assistant, user, result).
 */
export function mapEvent(raw: SdkMessage): Record<string, unknown>[] {
  const type = raw.type as string | undefined;

  switch (type) {
    // Text deltas arrive inside stream_event → content_block_delta
    case 'stream_event': {
      const event = raw.event as Record<string, unknown> | undefined;
      if (!event) return [];
      if (event.type === 'content_block_delta') {
        const delta = event.delta as Record<string, unknown> | undefined;
        if (delta?.type === 'text_delta') {
          return [{
            type: 'content_block_delta',
            delta: delta.text ?? '',
            parent_tool_use_id: raw.parent_tool_use_id,
          }];
        }
      }
      // Detect tool_use blocks from content_block_start
      if (event.type === 'content_block_start') {
        const contentBlock = event.content_block as Record<string, unknown> | undefined;
        if (contentBlock?.type === 'tool_use') {
          return [{
            type: 'content_block_start',
            tool_call_id: contentBlock.id,
            tool_name: contentBlock.name,
            tool_input: contentBlock.input,
            parent_tool_use_id: raw.parent_tool_use_id,
          }];
        }
      }
      // Forward other stream events (content_block_stop, etc.)
      return [{ type: 'stream_event', event, parent_tool_use_id: raw.parent_tool_use_id }];
    }

    // Complete assistant turn — contains text blocks and tool_use blocks
    case 'assistant': {
      const message = raw.message as Record<string, unknown> | undefined;
      const content = message?.content as Array<Record<string, unknown>> | undefined;
      if (!Array.isArray(content)) return [];
      // Emit ALL tool_use blocks as content_block_start events for the UI
      const results: Record<string, unknown>[] = [];
      for (const block of content) {
        if (block.type === 'tool_use') {
          results.push({
            type: 'content_block_start',
            tool_call_id: block.id,
            tool_name: block.name,
            tool_input: block.input,
          });
        }
      }
      return results;
    }

    // Tool results from the SDK
    case 'user': {
      const message = raw.message as Record<string, unknown> | undefined;
      const content = message?.content as Array<Record<string, unknown>> | undefined;
      if (!Array.isArray(content)) return [];
      // Emit ALL tool_result blocks
      const results: Record<string, unknown>[] = [];
      for (const block of content) {
        if (block.type === 'tool_result') {
          // SDK tool_result content is an array of [{type: 'text', text: '...'}] — extract to string
          const rawContent = block.content;
          const outputText = typeof rawContent === 'string'
            ? rawContent
            : Array.isArray(rawContent)
              ? rawContent.filter((c: Record<string, unknown>) => c.type === 'text').map((c: Record<string, unknown>) => c.text).join('')
              : String(rawContent ?? '');
          // Try parsing tool output as JSON — if successful, stash the parsed object
          // in `result` so registry components (e.g. SecretsApprovalAdapter) can
          // access structured data like api_requests, summary, justification.
          let result: Record<string, unknown> | undefined;
          try {
            const parsed = JSON.parse(outputText);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              result = parsed;
            }
          } catch { /* not JSON — that's fine */ }
          results.push({
            type: 'tool_result',
            tool_call_id: block.tool_use_id,
            output: outputText,
            ...(result ? { result } : {}),
          });
        }
      }
      return results;
    }

    // Status events from the agent (e.g., session reconnecting)
    case 'status': {
      return [{ type: 'status', status: (raw.status as string) ?? 'working' }];
    }

    // Session init — metadata only, no visible UI element
    case 'system':
      return [];

    // Final result — session end
    case 'result': {
      const subtype = raw.subtype as string | undefined;
      if (subtype === 'success') {
        return [{ type: 'message_stop' }];
      }
      return [{
        type: 'error',
        message: (raw.errors as string[])?.join(', ') ?? 'Agent error',
        recoverable: false,
      }];
    }

    // API-injected error
    case undefined: {
      // Handle ping and error events from the API layer
      const event = raw.event as string | undefined;
      if (event === 'error') {
        const data = raw.data as Record<string, unknown> | undefined;
        return [{
          type: 'error',
          message: data?.message ?? 'Stream error',
          recoverable: data?.recoverable ?? false,
          recovery_hint: data?.recovery_hint as string | undefined,
        }];
      }
      return [];
    }

    default:
      // Forward unknown SDK message types as-is
      return [raw];
  }
}

/**
 * Map a batch of raw SDK events from DB replay into StreamRenderer-compatible events.
 * Unlike mapEvent() which handles one live event at a time, this processes the full
 * stored sdk_events array with deduplication and multi-tool support.
 */
export function mapEventsForReplay(rawEvents: SdkMessage[]): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];
  // Track tool_call_id → index in result[] so the assistant message can patch
  // the tool_input with the complete value (stream_event fires first with empty input).
  const toolCallIdToIndex = new Map<string, number>();
  const seenToolResultIds = new Set<string>();

  for (const raw of rawEvents) {
    const type = raw.type as string | undefined;

    switch (type) {
      case 'stream_event': {
        const event = raw.event as Record<string, unknown> | undefined;
        if (!event) break;

        if (event.type === 'content_block_delta') {
          const delta = event.delta as Record<string, unknown> | undefined;
          if (delta?.type === 'text_delta') {
            result.push({
              type: 'content_block_delta',
              delta: delta.text ?? '',
              parent_tool_use_id: raw.parent_tool_use_id,
            });
          }
        } else if (event.type === 'content_block_start') {
          const contentBlock = event.content_block as Record<string, unknown> | undefined;
          if (contentBlock?.type === 'tool_use') {
            const toolId = contentBlock.id as string;
            if (!toolCallIdToIndex.has(toolId)) {
              toolCallIdToIndex.set(toolId, result.length);
              result.push({
                type: 'content_block_start',
                tool_call_id: toolId,
                tool_name: contentBlock.name,
                tool_input: contentBlock.input,
                parent_tool_use_id: raw.parent_tool_use_id,
              });
            }
          }
        }
        // Drop other stream_event types (content_block_stop, message_start, etc.) — no UI representation
        break;
      }

      case 'assistant': {
        const message = raw.message as Record<string, unknown> | undefined;
        const content = message?.content as Array<Record<string, unknown>> | undefined;
        if (!Array.isArray(content)) break;
        for (const block of content) {
          if (block.type === 'tool_use') {
            // Text blocks are SKIPPED — stream_event > content_block_delta already has the text.
            // For tool_use blocks: if stream_event already emitted a content_block_start for this
            // tool_call_id (with empty tool_input = {}), patch it with the complete tool_input from
            // the assistant summary message. Otherwise, add a new entry (fallback for old stored events
            // that lack stream_event entries).
            const toolId = block.id as string;
            const existingIdx = toolCallIdToIndex.get(toolId);
            if (existingIdx !== undefined) {
              // Patch the existing entry with the complete tool_input and parent_tool_use_id
              result[existingIdx] = {
                ...result[existingIdx],
                tool_input: block.input,
                ...(raw.parent_tool_use_id ? { parent_tool_use_id: raw.parent_tool_use_id } : {}),
              };
            } else {
              toolCallIdToIndex.set(toolId, result.length);
              result.push({
                type: 'content_block_start',
                tool_call_id: toolId,
                tool_name: block.name,
                tool_input: block.input,
                ...(raw.parent_tool_use_id ? { parent_tool_use_id: raw.parent_tool_use_id } : {}),
              });
            }
          }
        }
        break;
      }

      case 'user': {
        const message = raw.message as Record<string, unknown> | undefined;
        const content = message?.content as Array<Record<string, unknown>> | undefined;
        if (!Array.isArray(content)) break;
        for (const block of content) {
          if (block.type === 'tool_result') {
            const toolId = block.tool_use_id as string;
            if (!seenToolResultIds.has(toolId)) {
              seenToolResultIds.add(toolId);
              // SDK tool_result content is an array of [{type: 'text', text: '...'}] — extract to string
              const rawContent = block.content;
              let outputText: string;
              if (typeof rawContent === 'string') {
                outputText = rawContent;
              } else if (Array.isArray(rawContent)) {
                outputText = rawContent
                  .filter((c: Record<string, unknown>) => c.type === 'text')
                  .map((c: Record<string, unknown>) => c.text)
                  .join('');
              } else {
                outputText = String(rawContent ?? '');
              }
              // Try parsing tool output as JSON for structured result data
              let parsedResult: Record<string, unknown> | undefined;
              try {
                const parsed = JSON.parse(outputText);
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                  parsedResult = parsed;
                }
              } catch { /* not JSON */ }
              result.push({
                type: 'tool_result',
                tool_call_id: toolId,
                output: outputText,
                ...(parsedResult ? { result: parsedResult } : {}),
              });
            }
          }
        }
        break;
      }

      case 'system':
      case 'status':
        break;

      case 'result': {
        const subtype = raw.subtype as string | undefined;
        if (subtype === 'success') {
          result.push({ type: 'message_stop' });
        } else {
          result.push({
            type: 'error',
            message: (raw.errors as string[])?.join(', ') ?? 'Agent error',
            recoverable: false,
          });
        }
        break;
      }

      default:
        break;
    }
  }

  return result;
}

type StreamStatus = 'idle' | 'loading' | 'streaming' | 'success' | 'error' | 'interrupted';

export function useStreamMessages() {
  const apiClient = useApiClient();
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [events, setEvents] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState<string | undefined>();
  const activeController = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    activeController.current?.abort();
    activeController.current = null;
    setStatus('idle');
    setEvents([]);
    setError(undefined);
  }, []);

  const sendMessage = useCallback(
    async (
      conversationId: string,
      content: string,
      options?: { signal?: AbortSignal; file_ids?: string[]; stream_events?: string[] },
    ) => {
      // Abort any previous in-flight stream
      activeController.current?.abort();
      const controller = new AbortController();
      activeController.current = controller;

      // If caller provided a signal, abort our controller when it fires
      if (options?.signal) {
        options.signal.addEventListener('abort', () => controller.abort(), { once: true });
      }

      setStatus('loading');
      setEvents([]);
      setError(undefined);

      let response: Response;
      try {
        response = await apiClient.sendMessageStream(conversationId, content, {
          signal: controller.signal,
          ...(options?.file_ids ? { file_ids: options.file_ids } : {}),
          ...(options?.stream_events ? { stream_events: options.stream_events } : {}),
        });
      } catch (err: unknown) {
        const message =
          err instanceof Error || err instanceof DOMException
            ? err.message
            : 'Fetch failed';
        setStatus('error');
        setError(message);
        return { status: 'error' as const, events: [] as Record<string, unknown>[], error: message };
      }

      if (!response.ok) {
        let detail = `Request failed with status ${response.status}`;
        try {
          const body = await response.json();
          if (body.detail) detail = body.detail;
        } catch {
          /* non-JSON error body */
        }
        setStatus('error');
        setError(detail);
        return { status: 'error' as const, events: [] as Record<string, unknown>[], error: detail };
      }

      if (!response.body) {
        setStatus('error');
        setError('Response body is empty');
        return { status: 'error' as const, events: [] as Record<string, unknown>[], error: 'Response body is empty' };
      }

      const { parseNdjsonStream } = await import('../lib/ndjson-parser');
      const accumulated: Record<string, unknown>[] = [];
      let errorMessage: string | undefined;
      let flushScheduled = false;
      let rafHandle = 0;
      let fallbackHandle = 0;

      // Dedup content_block_start events by tool_call_id — the SDK emits one early from
      // stream_event (with empty tool_input = {}) and a second from the assistant message
      // (with the complete tool_input). Keep the last occurrence so the card shows full IN/OUT.
      const dedupeToolCards = (events: Record<string, unknown>[]): Record<string, unknown>[] => {
        const result: Record<string, unknown>[] = [];
        const seenIdx = new Map<string, number>(); // tool_call_id → index in result
        for (const e of events) {
          if (e.type === 'content_block_start' && typeof e.tool_call_id === 'string') {
            const existing = seenIdx.get(e.tool_call_id);
            if (existing !== undefined) {
              result[existing] = e; // Replace with later (complete) version
            } else {
              seenIdx.set(e.tool_call_id, result.length);
              result.push(e);
            }
          } else {
            result.push(e);
          }
        }
        return result;
      };

      const flushEvents = () => {
        flushScheduled = false;
        cancelAnimationFrame(rafHandle);
        clearTimeout(fallbackHandle);
        // Don't flush if aborted — abort() already cleared state
        if (controller.signal.aborted) return;
        setEvents(dedupeToolCards(accumulated));
      };

      setStatus('streaming');

      for await (const raw of parseNdjsonStream<SdkMessage>(response.body)) {
        // Heartbeat events keep the CF proxy alive — discard silently
        if (raw.event === 'ping') continue;
        const mapped = mapEvent(raw);
        if (mapped.length === 0) continue;
        accumulated.push(...mapped);
        for (const evt of mapped) {
          if (evt.type === 'error') {
            errorMessage = evt.message as string;
          }
        }
        // Batch state updates: flush at next animation frame (~60fps) for smooth
        // incremental streaming while bounding React re-renders.
        // A 500ms setTimeout fallback ensures flushes still happen when the tab is
        // backgrounded (Chrome throttles/suspends rAF for hidden tabs).
        if (!flushScheduled) {
          flushScheduled = true;
          rafHandle = requestAnimationFrame(flushEvents);
          fallbackHandle = window.setTimeout(flushEvents, 500);
        }
      }

      // Cancel any pending callbacks — the final explicit flush below is authoritative
      cancelAnimationFrame(rafHandle);
      clearTimeout(fallbackHandle);

      // If aborted while streaming, do not overwrite abort()'s idle state
      if (controller.signal.aborted) {
        return { status: 'error' as const, events: [] as Record<string, unknown>[], error: 'Aborted' };
      }

      // Detect premature stream closure — if no message_stop event arrived, the
      // connection was interrupted before the agent finished. Return 'interrupted'
      // status so the caller can enter extended session mode (poll for completion).
      const hasMessageStop = accumulated.some((e) => e.type === 'message_stop');
      if (!hasMessageStop && !errorMessage) {
        accumulated.push({
          type: 'status',
          status: 'extended_session',
          message: 'Agent is still working in the background. Results will appear here when ready.',
        });

        // Final flush to ensure all events are in state
        flushEvents();

        const finalEvents = dedupeToolCards(accumulated);
        setStatus('interrupted');
        return { status: 'interrupted' as const, events: finalEvents };
      }

      // Final flush to ensure all events are in state
      flushEvents();

      const finalEvents = dedupeToolCards(accumulated);
      if (errorMessage) {
        setStatus('error');
        setError(errorMessage);
        return { status: 'error' as const, events: finalEvents, error: errorMessage };
      }
      setStatus('success');
      return { status: 'success' as const, events: finalEvents };
    },
    [apiClient],
  );

  return { status, events, error, sendMessage, abort };
}
